import firestore from '@react-native-firebase/firestore';
import {
  Truck,
  CreateTruckData,
  UpdateTruckData,
} from '../types/Truck';
import { ErrorLogger } from '../utils/errorLogger';
import { mapTruckDoc } from '../utils/mappers';

const TRUCKS_COLLECTION = 'trucks';

export class TruckService {
  private static getTrucksCollection() {
    return firestore().collection(TRUCKS_COLLECTION);
  }

  /**
   * Create a new truck
   */
  static async createTruck(
    userId: string,
    truckData: CreateTruckData,
  ): Promise<Truck> {
    try {
      const now = new Date();
      const truck: Omit<Truck, 'id'> = {
        userId,
        plateNumber: truckData.plateNumber,
        model: truckData.model,
        color: truckData.color,
        isActive: truckData.isActive ?? true,
        createdAt: now,
        updatedAt: now,
      };

      const docRef = await this.getTrucksCollection().add(truck);

      const createdTruck: Truck = {
        id: docRef.id,
        ...truck,
      };

      console.log('Truck created successfully:', createdTruck.id);
      return createdTruck;
    } catch (error) {
      ErrorLogger.logError(error, 'TruckService.createTruck');
      throw new Error('Failed to create truck');
    }
  }

  /**
   * Get all active trucks in the fleet. Trucks are a shared resource — not
   * scoped to the caller — so every fleet member sees the same list.
   */
  static async getAllActiveTrucks(): Promise<Truck[]> {
    try {
      const snapshot = await this.getTrucksCollection()
        .where('isActive', '==', true)
        .get();

      return snapshot.docs
        .map(doc => mapTruckDoc(doc))
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    } catch (error) {
      ErrorLogger.logError(error, 'TruckService.getAllActiveTrucks');
      return []; // Return empty array instead of throwing
    }
  }

  /**
   * Get a specific truck by ID
   */
  static async getTruckById(truckId: string): Promise<Truck | null> {
    try {
      const doc = await this.getTrucksCollection().doc(truckId).get();

      if (!doc.exists) {
        return null;
      }

      const truck: Truck = mapTruckDoc(doc as any);

      return truck;
    } catch (error) {
      ErrorLogger.logError(error, 'TruckService.getTruckById');
      throw new Error('Failed to fetch truck');
    }
  }

  /**
   * Update an existing truck
   */
  static async updateTruck(
    truckId: string,
    updates: UpdateTruckData,
  ): Promise<void> {
    try {
      const updateData = {
        ...updates,
        updatedAt: new Date(),
      };

      await this.getTrucksCollection().doc(truckId).update(updateData);
      console.log('Truck updated successfully:', truckId);
    } catch (error) {
      ErrorLogger.logError(error, 'TruckService.updateTruck');
      throw new Error('Failed to update truck');
    }
  }

  /**
   * Delete a truck
   */
  static async deleteTruck(truckId: string): Promise<void> {
    try {
      await this.getTrucksCollection().doc(truckId).delete();
      console.log('Truck deleted successfully:', truckId);
    } catch (error) {
      ErrorLogger.logError(error, 'TruckService.deleteTruck');
      throw new Error('Failed to delete truck');
    }
  }

  /**
   * Subscribe to real-time updates for the whole fleet's trucks. Trucks are a
   * shared resource, so this is not scoped to any single user.
   */
  static subscribeToAllTrucks(
    onUpdate: (trucks: Truck[]) => void,
    _onError?: (error: Error) => void,
  ): () => void {
    const unsubscribe = this.getTrucksCollection().onSnapshot(
      snapshot => {
        const trucks: Truck[] = snapshot.docs
          .map(doc => mapTruckDoc(doc))
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

        onUpdate(trucks);
      },
      error => {
        ErrorLogger.logError(error, 'TruckService.subscribeToAllTrucks');
        // Don't call onError to prevent infinite loops, just log
        onUpdate([]); // Provide empty array as fallback
      },
    );

    return unsubscribe;
  }

  /**
   * Toggle truck active status
   */
  static async toggleTruckStatus(truckId: string): Promise<void> {
    try {
      const truck = await this.getTruckById(truckId);
      if (!truck) {
        throw new Error('Truck not found');
      }

      await this.updateTruck(truckId, {
        isActive: !truck.isActive,
      });

      console.log('Truck status toggled successfully:', truckId);
    } catch (error) {
      ErrorLogger.logError(error, 'TruckService.toggleTruckStatus');
      throw new Error('Failed to toggle truck status');
    }
  }
}

export default TruckService;
